import { OrderType } from '../../services/exports/Constants';
import useGiftCardCheckout, {
  useGenerateGiftCardReturnUrl,
  useHandleGiftCardCheckoutResult,
} from './useGiftCardCheckout';
import useOrderCheckout, { useGenerateOrderReturnUrl, useHandleOrderCheckoutResult } from './useOrderCheckout';
import { GenericOrder, PaymentResult } from '../../services/exports/Interfaces';
import { PaymentIntent, PaymentRequestPaymentMethodEvent } from '@stripe/stripe-js';

export type GenerateOrderReturnUrlFunction = (replace?: { [key: string]: string }) => string;

export enum PaymentReturnUrlPlaceholder {
  OrderUUID = '{placeholder::order_uuid}',
  PaymentGateway = '{placeholder::payment_gateway}',
}

export type ConfirmPaymentFunctionExtraParams = { paymentRequestPaymentMethodEvent?: PaymentRequestPaymentMethodEvent };
export type ConfirmPaymentFunction = (
  order: GenericOrder,
  paymentIntent?: PaymentIntent,
  extra?: ConfirmPaymentFunctionExtraParams,
) => Promise<PaymentResult>;

/**
 * A cancelled 3DS challenge is Failed for the order (it is abandoned) but it is the customer's
 * decision: the handler still alerts and releases the button, yet logs a breadcrumb, not a
 * Sentry error.
 */
export interface PaymentErrorOptions {
  cancelled?: boolean;
  /**
   * The customer is back on our return page from an offsite payment the bank refused: nothing
   * on that page can take a new payment, so they are sent back to the payment step, as an
   * Adyen redirect already is.
   */
  backToCheckout?: boolean;
  /**
   * The page the payment was on is gone (the customer navigated away with a sheet open): the
   * button lets go, but nothing is shown and nowhere is navigated to — there is nobody there.
   */
  silent?: boolean;
}

export type OnOrderPaymentErrorFunction = (options?: PaymentErrorOptions) => boolean;
export type OnOrderPaymentSuccessFunction = (order: GenericOrder) => boolean;

export function useGenerateReturnUrl(orderType: OrderType) {
  const { generateReturnUrl: generateOrderReturnUrl } = useGenerateOrderReturnUrl();
  const { generateReturnUrl: generateGiftCardReturnUrl } = useGenerateGiftCardReturnUrl();

  return {
    generateReturnUrl: orderType === OrderType.GiftCard ? generateGiftCardReturnUrl : generateOrderReturnUrl,
  };
}

export function useHandleCheckoutResult(
  orderType: OrderType,
  props: any,
): { onPaymentError: OnOrderPaymentErrorFunction; onPaymentSuccess: OnOrderPaymentSuccessFunction } {
  const { onPaymentError: onOrderPaymentError, onPaymentSuccess: onOrderPaymentSuccess } =
    useHandleOrderCheckoutResult(props);

  const { onPaymentError: onGiftCardPaymentError, onPaymentSuccess: onGiftCardPaymentSuccess } =
    useHandleGiftCardCheckoutResult(props);

  return {
    onPaymentError: orderType === OrderType.GiftCard ? onGiftCardPaymentError : onOrderPaymentError,
    onPaymentSuccess: orderType === OrderType.GiftCard ? onGiftCardPaymentSuccess : onOrderPaymentSuccess,
  };
}

export default function useCheckout(orderType: OrderType, props: any) {
  const { checkout: orderCheckout } = useOrderCheckout(props);
  const { checkout: giftCardCheckout } = useGiftCardCheckout(props);

  return {
    checkout: orderType === OrderType.GiftCard ? giftCardCheckout : orderCheckout,
  };
}
